import { API_BASE_URL } from "./site_urls";

// ---------------------------------------------------------------------------
// Application constants
// ---------------------------------------------------------------------------

export const APPLICATION_NAME = "LeadNova";
export const APPLICATION_VERSION = "1.0.0";

/** localStorage key for persisted auth state */
export const STORAGE = "leadnova_user";

/** Default items per page for paginated tables */
export const PER_PAGE = 25;

/** Effectively unlimited — use sparingly */
export const WITHOUT_PER_PAGE = 9999;

export const DATE_FORMAT = "MMM d, yyyy";

// ---------------------------------------------------------------------------
// Route URLs (must match App.tsx route definitions)
// ---------------------------------------------------------------------------

export const URLS = {
  HOME: "/",
  AUTH: {
    LOGIN: "/login",
    SIGNUP: "/signup",
    FORGOT_PASSWORD: "/forgot-password",
    RESET_PASSWORD: "/reset-password",
  },
  APP: {
    DASHBOARD: "/app/dashboard",
    LEADS: "/app/leads",
    CAMPAIGNS: "/app/campaigns",
    SEQUENCES: "/app/sequences",
    WORKFLOWS: "/app/workflows",
    WORKFLOW_EDIT: (id: string) => `/app/workflows/${id}/edit`,
    REPLIES: "/app/replies",
    ANALYTICS: "/app/analytics",
    SETTINGS: "/app/settings",
  },
};

// ---------------------------------------------------------------------------
// API endpoint URLs
// ---------------------------------------------------------------------------

export const API_URL = {
  AUTH: {
    LOGIN: `${API_BASE_URL}auth/login`,
    REGISTER: `${API_BASE_URL}auth/register`,
    LOGOUT: `${API_BASE_URL}auth/logout`,
    REFRESH: `${API_BASE_URL}auth/refresh`,
    ME: `${API_BASE_URL}auth/me`,
    FORGOT_PASSWORD: `${API_BASE_URL}auth/forgot-password`,
    RESET_PASSWORD: `${API_BASE_URL}auth/reset-password`,
  },
  LEADS: {
    LIST: `${API_BASE_URL}leads`,
    CREATE: `${API_BASE_URL}leads`,
    FILTER_OPTIONS: `${API_BASE_URL}leads/filter-options`,
    GET: (id: string) => `${API_BASE_URL}leads/${id}`,
    SEGMENTS: (id: string) => `${API_BASE_URL}leads/${id}/segments`,
    NOTES: (id: string) => `${API_BASE_URL}leads/${id}/notes`,
    DELETE_NOTE: (leadId: string, noteId: string) => `${API_BASE_URL}leads/${leadId}/notes/${noteId}`,
    UPDATE: (id: string) => `${API_BASE_URL}leads/${id}`,
    DELETE: (id: string) => `${API_BASE_URL}leads/${id}`,
    IMPORT: `${API_BASE_URL}leads/import`,
    EXPORT: `${API_BASE_URL}leads/export`,
    REVEAL: (id: string) => `${API_BASE_URL}leads/${id}/reveal`,
  },
  SEGMENTS: {
    LIST: `${API_BASE_URL}segments`,
    CREATE: `${API_BASE_URL}segments`,
    GET: (id: string) => `${API_BASE_URL}segments/${id}`,
    DELETE: (id: string) => `${API_BASE_URL}segments/${id}`,
    LEADS: (id: string) => `${API_BASE_URL}segments/${id}/leads`,
    ADD_LEADS: (id: string) => `${API_BASE_URL}segments/${id}/leads`,
    REMOVE_LEADS: (id: string) => `${API_BASE_URL}segments/${id}/leads`,
    FILTERED_COUNT: (id: string) => `${API_BASE_URL}segments/${id}/filtered-count`,
  },
  CAMPAIGNS: {
    LIST: `${API_BASE_URL}campaigns`,
    CREATE: `${API_BASE_URL}campaigns`,
    GET: (id: string) => `${API_BASE_URL}campaigns/${id}`,
    UPDATE: (id: string) => `${API_BASE_URL}campaigns/${id}`,
    DELETE: (id: string) => `${API_BASE_URL}campaigns/${id}`,
    DRAFT: (id: string) => `${API_BASE_URL}campaigns/${id}/draft`,
    STATUS: (id: string) => `${API_BASE_URL}campaigns/${id}/status`,
    DUPLICATE: (id: string) => `${API_BASE_URL}campaigns/${id}/duplicate`,
    LEADS: (id: string) => `${API_BASE_URL}campaigns/${id}/leads`,
    GOALS: `${API_BASE_URL}campaign-goals`,
    ANALYTICS: (id: string) => `${API_BASE_URL}campaigns/${id}/analytics`,
    CONTACTS: (id: string) => `${API_BASE_URL}campaigns/${id}/contacts`,
    EMAILS: (id: string) => `${API_BASE_URL}campaigns/${id}/emails`,
    ACTIVITY: (id: string) => `${API_BASE_URL}campaigns/${id}/activity`,
  },
  SEQUENCES: {
    LIST: `${API_BASE_URL}sequences`,
    CREATE: `${API_BASE_URL}sequences`,
    GET: (id: string) => `${API_BASE_URL}sequences/${id}`,
    UPDATE: (id: string) => `${API_BASE_URL}sequences/${id}`,
    DELETE: (id: string) => `${API_BASE_URL}sequences/${id}`,
    ACTIVATE: (id: string) => `${API_BASE_URL}sequences/${id}/activate`,
    PAUSE: (id: string) => `${API_BASE_URL}sequences/${id}/pause`,
    DUPLICATE: (id: string) => `${API_BASE_URL}sequences/${id}/duplicate`,
    ARCHIVE: (id: string) => `${API_BASE_URL}sequences/${id}/archive`,
    STEPS: (id: string) => `${API_BASE_URL}sequences/${id}/steps`,
    STEP: (id: string, stepId: string) => `${API_BASE_URL}sequences/${id}/steps/${stepId}`,
    REORDER: (id: string) => `${API_BASE_URL}sequences/${id}/steps/reorder`,
    VARIANTS: (stepId: string) => `${API_BASE_URL}steps/${stepId}/variants`,
    VARIANT: (stepId: string, variantId: string) => `${API_BASE_URL}steps/${stepId}/variants/${variantId}`,
    LEADS: (id: string) => `${API_BASE_URL}sequences/${id}/leads`,
    LEAD: (id: string, leadId: string) => `${API_BASE_URL}sequences/${id}/leads/${leadId}`,
    STAR: (id: string) => `${API_BASE_URL}sequences/${id}/star`,
    UNSTAR: (id: string) => `${API_BASE_URL}sequences/${id}/unstar`,
    EMAIL_PREVIEW: `${API_BASE_URL}sequences/email-preview`,
    ANALYTICS: (id: string) => `${API_BASE_URL}sequences/${id}/analytics`,
  },
  EMAIL_IMAGES: `${API_BASE_URL}email-images`,
  WORKFLOWS: {
    LIST:           `${API_BASE_URL}workflows`,
    CREATE:         `${API_BASE_URL}workflows`,
    GET:            (id: string) => `${API_BASE_URL}workflows/${id}`,
    UPDATE:         (id: string) => `${API_BASE_URL}workflows/${id}`,
    DELETE:         (id: string) => `${API_BASE_URL}workflows/${id}`,
    ACTIVATE:       (id: string) => `${API_BASE_URL}workflows/${id}/activate`,
    PAUSE:          (id: string) => `${API_BASE_URL}workflows/${id}/pause`,
    DUPLICATE:      (id: string) => `${API_BASE_URL}workflows/${id}/duplicate`,
    FILTERS:        (id: string) => `${API_BASE_URL}workflows/${id}/filters`,
    LEADS:          (id: string) => `${API_BASE_URL}workflows/${id}/leads`,
    LEADS_PREVIEW:  (id: string) => `${API_BASE_URL}workflows/${id}/leads/preview`,
  },
  RECENTLY_VIEWED: {
    LIST:   `${API_BASE_URL}recently-viewed`,
    RECORD: `${API_BASE_URL}recently-viewed`,
    CLEAR:  `${API_BASE_URL}recently-viewed`,
  },
  ANALYTICS: {
    OVERVIEW:     `${API_BASE_URL}analytics/overview`,
    DASHBOARD:    `${API_BASE_URL}analytics/dashboard`,
    LEAD_GROWTH:  `${API_BASE_URL}analytics/lead-growth`,
    CAMPAIGNS:    `${API_BASE_URL}analytics/campaigns`,
  },
  SETTINGS: {
    PROFILE: `${API_BASE_URL}settings/profile`,
    ORGANIZATION: `${API_BASE_URL}settings/organization`,
    BILLING: `${API_BASE_URL}settings/billing`,
    EMAIL_ACCOUNTS: `${API_BASE_URL}settings/email-accounts`,
  },
  BILLING: `${API_BASE_URL}billing`,
  SUBSCRIPTION: `${API_BASE_URL}subscription`,
  TRANSACTIONS: `${API_BASE_URL}transactions`,
  PRICING: `${API_BASE_URL}pricing`,
  HEALTH: `${API_BASE_URL}health`,
  INBOX: {
    LIST:    `${API_BASE_URL}inbox`,
    FILTERS: `${API_BASE_URL}inbox/filters`,
    SYNC:    `${API_BASE_URL}inbox/sync`,
    GET:     (id: string) => `${API_BASE_URL}inbox/${id}`,
    REPLY:   (id: string) => `${API_BASE_URL}inbox/${id}/reply`,
    STATUS:  (id: string) => `${API_BASE_URL}inbox/${id}/status`,
    STAR:    (id: string) => `${API_BASE_URL}inbox/${id}/star`,
    READ:    (id: string) => `${API_BASE_URL}inbox/${id}/read`,
    SUGGEST_REPLY: (id: string) => `${API_BASE_URL}inbox/${id}/suggest-reply`,
  },
  SUPPORT: {
    TICKETS: `${API_BASE_URL}support-tickets`,
    TICKET:  (id: string) => `${API_BASE_URL}support-tickets/${id}`,
    SITE_SETTINGS: `${API_BASE_URL}settings/site`,
  },
};

// ---------------------------------------------------------------------------
// Redux ACTIONS — kept minimal: auth + loader only. Redux is frozen at auth.
// New features must use TanStack Query, not extend Redux.
// ---------------------------------------------------------------------------

export const ACTIONS = {
  LOADER: {
    SET_FP_STATUS: "ACTIONS/LOADER/SET_FP_STATUS",
  },
  AUTH: {
    LOGIN: "ACTIONS/USER/LOGIN",
    ME: "ACTIONS/USER/ME",
    LOGOUT: "ACTIONS/USER/LOGOUT",
    PROFILE: "ACTIONS/USER/PROFILE",
    UPDATE_PROFILE: "ACTIONS/USER/UPDATE_PROFILE",
  },
};

// ---------------------------------------------------------------------------
// Redux SAGA_ACTIONS — kept minimal: auth only.
// ---------------------------------------------------------------------------

export const SAGA_ACTIONS = {
  AUTH: {
    LOGIN: "SAGA_ACTIONS/AUTH/LOGIN",
    REGISTRATION: "SAGA_ACTIONS/AUTH/REGISTRATION",
    FORGOT_PASSWORD: "SAGA_ACTIONS/AUTH/FORGOT_PASSWORD",
    RESET_PASSWORD: "SAGA_ACTIONS/AUTH/RESET_PASSWORD",
    LOGOUT: "SAGA_ACTIONS/AUTH/LOGOUT",
    DETAILS: "SAGA_ACTIONS/AUTH/DETAILS",
    UPDATE_PROFILE: "SAGA_ACTIONS/AUTH/UPDATE_PROFILE",
  },
};

// Re-export utility modules
export * from "./site_statics";
export * from "./site_urls";
export * from "./functions";
